import { get } from 'lodash-es';
import { getWorkspaceIdFromCurrentUrl } from '@/utils/workspace-id';
import { getAppVersion } from '@/utils/version';
import { isEmptyString } from '@/utils/string';
import type { AppRoute, AppRouter, InitClientArgs } from '@ts-rest/core';
import type { ClientInferResponseBody, PartialClientInferRequest } from '@ts-rest/core';
import { initClient, tsRestFetchApi } from '@ts-rest/core';
import {
  useQuery as useTanStackQuery,
  useMutation as useTanStackMutation,
  useInfiniteQuery as useTanStackInfiniteQuery,
} from '@tanstack/react-query';
import type { UseQueryOptions, UseMutationOptions, InfiniteData } from '@tanstack/react-query';
import Cookies from 'universal-cookie';
import { ACTIVE_SESSION_HEADER, activeSessionIdStore } from '@customerio/hydra-session-state';

export type { ClientInferResponseBody as ResponseBody } from '@ts-rest/core';

/**
 * Type for the ts-rest fetch API response
 */
export type TsRestResponse = Awaited<ReturnType<typeof tsRestFetchApi>>;

/**
 * Custom error class for API errors with the full response
 */
export class APIError extends Error {
  response: TsRestResponse;

  constructor(message: string, response: TsRestResponse) {
    super(message);
    this.name = 'APIError';
    this.response = response;
  }
}

/**
 * Type guard to check if an error is an instance of APIError.
 *
 * Usage:
 * ```ts
 * if (isAPIError<ClientInferResponseBody<typeof testContract.getUser, 200>>(error)) {
 *   // Handle APIError with specific response body type
 * }
 * ```
 */
export function isAPIError<ResponseBody = unknown>(
  error: unknown,
): error is APIError & {
  response: TsRestResponse & {
    body: ResponseBody;
  };
} {
  return error instanceof APIError;
}

/**
 * Creates a basic http api client and a tanstack react query client
 * for the given ts-rest contract and options.
 *
 * Example usage:
 * ```ts
 * import { createApi } from '@/services/api/ts-rest';
 * import { appRouter } from '@/services/api/contract';
 * const { api, query } = createApi(appRouter, {
 *  baseUrl: '/api',
 * });
 *
 * const user = await api.getUser({ ... });
 *
 * or
 *
 * const { data: user } = query.getUser.useQuery({ ... });
 */
/**
 * Rebuild a URL path's query string so that array values use repeated keys
 * instead of ts-rest's default bracket-indexed format.
 *
 * ## Why this exists
 *
 * ts-rest serializes array query params with bracket indices:
 *   `data_type[0]=campaign&data_type[1]=newsletter`
 *
 * Our Go backend reads arrays via `request.Form["key"]` which expects
 * repeated keys:
 *   `data_type=campaign&data_type=newsletter`
 *
 * With bracket indices, Go sees `data_type[0]` and `data_type[1]` as
 * separate keys — `request.Form["data_type"]` returns an empty slice.
 *
 * ## How it works
 *
 * 1. If rawQuery is nil or has no array values, return the path unchanged
 *    (zero overhead for the common case — most endpoints use only scalars).
 * 2. Otherwise, strip the query string that ts-rest built and rebuild it
 *    from the raw query object using `URLSearchParams.append()`, which
 *    produces the repeated-key format.
 *
 * ## Affected endpoints
 *
 * Currently only the analysis endpoint (`/metrics/analysis`) passes arrays
 * (`data_type`, `tag_ids`, `status`). No other hydra call site passes
 * array query params today, so this is a no-op for all other endpoints.
 *
 * @param path - The URL path with ts-rest's bracket-indexed query string
 * @param rawQuery - The original query object before ts-rest serialization
 *                   (available as `args.rawQuery` in the custom api handler)
 * @returns The path with a repeated-key query string, or unchanged if no arrays
 */
function rebuildPathWithRepeatedKeyQuery(path: string, rawQuery: unknown): string {
  if (rawQuery == null || typeof rawQuery !== 'object') {
    return path;
  }

  const hasArrayValues = Object.values(rawQuery as Record<string, unknown>).some((v) => Array.isArray(v));
  if (!hasArrayValues) {
    return path;
  }

  // Strip the existing query string that ts-rest built with bracket indices
  const basePath = path.split('?')[0];
  const params = new URLSearchParams();

  for (const [key, value] of Object.entries(rawQuery as Record<string, unknown>)) {
    if (value === undefined) continue;

    if (Array.isArray(value)) {
      for (const item of value) {
        params.append(key, String(item));
      }
    } else {
      params.append(key, String(value));
    }
  }

  const qs = params.toString();
  return qs.length > 0 ? `${basePath}?${qs}` : basePath;
}

/**
 * Extracts a human-readable message from an error response body.
 * Supports both `{ message }` and JSON:API `{ errors: [{ detail }] }` shapes.
 */
function extractErrorMessage(body: unknown, status: number): string {
  const message = get(body, 'message') ?? get(body, 'errors[0].detail');
  return message != null ? String(message) : `An unexpected error occured with HTTP code ${status}`;
}

export function createApi<T extends AppRouter>(
  contract: T,
  options: InitClientArgs & {
    /** Called per-request to supply additional headers (e.g. workspace ID). */
    getHeaders?: () => Record<string, string>;
  },
) {
  /**
   * The customApiHandler function is used to create a custom API handler
   * that can be used with ts-rest. It handles the API requests and responses,
   * including setting the appropriate headers
   */
  const baseApiHandler: InitClientArgs['api'] = async (args) => {
    const cookies = new Cookies();
    const accessToken = cookies.get('_access');
    const apiUrl = getApiUrl();

    // ts-rest serializes array query params with bracket indices (key[0]=val),
    // but our Go backend expects repeated keys (key=val&key=val). Rebuild the
    // query string from rawQuery using repeated keys so both formats work.
    const path = rebuildPathWithRepeatedKeyQuery(args.path, args.rawQuery);

    const response = await tsRestFetchApi({
      ...args,
      path: `${apiUrl}${path}`,
      fetchOptions: {
        ...args.fetchOptions,
        credentials: 'include',
      },
      headers: {
        ...args.headers,
        'x-ui-version': getAppVersion(),
        [ACTIVE_SESSION_HEADER]: activeSessionIdStore.get().toString(),
        ...(isEmptyString(accessToken) ? {} : { Authorization: `Bearer ${accessToken}` }),
        ...options.getHeaders?.(),
      },
    });

    // We throw an error if the response status is 400 or higher to make our control
    // flows simple (i.e. try/catch around the api calls).
    if (response.status >= 400) {
      throw new APIError(extractErrorMessage(response.body, response.status), response);
    }
    return response;
  };

  const { getHeaders: _, ...clientOptions } = options;

  const api = modifyApiToBeHappyPath(
    initClient(contract, {
      ...clientOptions,
      api: baseApiHandler,
    }),
  );

  return { api };
}

/**
 * Create type-safe query hooks from a ts-rest contract and API client.
 *
 * All types are derived from the contract (simple object type), not the API client
 * (complex proxy type). The API client is only used at runtime.
 *
 * @param _contract - The ts-rest contract (used for type inference only)
 * @param api - The API client to use at runtime (can be augmented with caching, etc.)
 */

// Extract GET method names from a contract
type GetMethods<T> = { [K in keyof T]: T[K] extends { method: 'GET' } ? K : never }[keyof T];
// Extract non-GET method names from a contract
type MutationMethods<T> = { [K in keyof T]: T[K] extends { method: 'GET' } ? never : K }[keyof T];

// Params type for a route (what the caller passes in)
type RouteParams<T> = T extends AppRoute ? PartialClientInferRequest<T> : never;
// Return type for a route (200 response body, after happy-path transform)
type RouteResponse<T> = T extends AppRoute ? ClientInferResponseBody<T, 200> : never;

// Precomputed lookup types avoid re-evaluating ClientInferResponseBody/PartialClientInferRequest
// at every call site, which triggers TS2590 "union type too complex" for large contracts.
type ResponseMap<T extends AppRouter> = { [K in keyof T]: RouteResponse<T[K]> };
type ParamsMap<T extends AppRouter> = { [K in keyof T]: RouteParams<T[K]> };

/**
 * When true, forwards TanStack Query’s `AbortSignal` into ts-rest `fetchOptions.signal`.
 * Use for fast-changing queries (e.g. typeahead search) where stale in-flight requests should cancel.
 * Default false so other callers keep prior behavior.
 *
 * https://tanstack.com/query/latest/docs/framework/react/guides/query-cancellation
 */
type ForwardAbortSignalOption = { forwardAbortSignal?: boolean };

/** Extra options for `useQuery` from this module (not passed to TanStack Query). */
type ServicesQueryHookOptions<TResponse, TData> = Omit<
  UseQueryOptions<TResponse, Error, TData>,
  'queryKey' | 'queryFn'
> &
  ForwardAbortSignalOption;

/**
 * Default retry: retry on 5xx server errors and non-API errors (e.g. network failures).
 * Don't retry 4xx client errors since they won't succeed on retry.
 */
export function shouldRetry(failureCount: number, error: Error): boolean {
  if (failureCount >= 3) return false;
  if (isAPIError(error)) {
    return error.response.status >= 500;
  }
  // Non-API errors (network failures, timeouts, etc.) — retry
  return true;
}

export function createQueryHooks<T extends AppRouter>(_contract: T, api: Record<string, unknown>) {
  const getQueryOptions = <TMethod extends GetMethods<T> & string, TData = ResponseMap<T>[TMethod]>(
    method: TMethod,
    params: ParamsMap<T>[TMethod],
    options?: ServicesQueryHookOptions<ResponseMap<T>[TMethod], TData>,
  ) => {
    const queryKey = [method, params] as const;
    const { forwardAbortSignal = false, ...tanStackOptions } = options ?? {};
    return {
      queryKey,
      queryFn:
        forwardAbortSignal === true
          ? ({ signal }: { signal: AbortSignal }) => {
              const request = params as ParamsMap<T>[TMethod] & { fetchOptions?: RequestInit };
              return (api[method] as (args: ParamsMap<T>[TMethod]) => Promise<ResponseMap<T>[TMethod]>)({
                ...request,
                fetchOptions: { ...request.fetchOptions, signal },
              });
            }
          : () => (api[method] as (args: ParamsMap<T>[TMethod]) => Promise<ResponseMap<T>[TMethod]>)(params),
      retry: shouldRetry,
      ...tanStackOptions,
    };
  };

  const useQuery = <TMethod extends GetMethods<T> & string, TData = ResponseMap<T>[TMethod]>(
    method: TMethod,
    params: ParamsMap<T>[TMethod],
    options?: ServicesQueryHookOptions<ResponseMap<T>[TMethod], TData>,
  ) => {
    return useTanStackQuery<ResponseMap<T>[TMethod], Error, TData>(getQueryOptions(method, params, options));
  };

  const useMutation = <TMethod extends MutationMethods<T> & string>(
    method: TMethod,
    options?: Omit<UseMutationOptions<ResponseMap<T>[TMethod], Error, ParamsMap<T>[TMethod]>, 'mutationFn'>,
  ) => {
    return useTanStackMutation<ResponseMap<T>[TMethod], Error, ParamsMap<T>[TMethod]>({
      mutationFn: (params) =>
        (api[method] as (params: ParamsMap<T>[TMethod]) => Promise<ResponseMap<T>[TMethod]>)(params),
      ...options,
    });
  };

  /**
   * Infinite query hook for paginated GET endpoints.
   *
   * The query key is `[method, ...queryKey]`. Pass stable discriminators (e.g. accountId)
   * via `queryKey` so different param sets get separate cache entries. Don't include
   * pagination params — TanStack manages pages internally.
   *
   * @example
   * useInfiniteQuery('getCopilotSessions', {
   *   queryKey: [accountId],
   *   initialPageParam: 1,
   *   queryData: ({ pageParam }) => ({ params: { accountId }, query: { page: pageParam } }),
   *   getNextPageParam: (lastPage) => lastPage.meta.nextPage,
   * })
   */
  const useInfiniteQuery = <TMethod extends GetMethods<T> & string, TPageParameter>(
    method: TMethod,
    options: {
      /** Additional key parts for cache discrimination (e.g. accountId). The full key is [method, ...queryKey]. */
      queryKey?: unknown[];
      initialPageParam: TPageParameter;
      queryData: (context: { pageParam: TPageParameter }) => ParamsMap<T>[TMethod];
      getNextPageParam: (
        lastPage: ResponseMap<T>[TMethod],
        allPages: ResponseMap<T>[TMethod][],
      ) => TPageParameter | undefined;
      getPreviousPageParam?: (
        firstPage: ResponseMap<T>[TMethod],
        allPages: ResponseMap<T>[TMethod][],
      ) => TPageParameter | undefined;
      enabled?: boolean;
    } & ForwardAbortSignalOption,
  ) => {
    type TResponse = ResponseMap<T>[TMethod];
    const { queryData, queryKey: extraKey = [], forwardAbortSignal = false, ...rest } = options;
    return useTanStackInfiniteQuery<TResponse, Error, InfiniteData<TResponse>, readonly unknown[], TPageParameter>({
      queryKey: [method, ...extraKey] as const,
      queryFn: ({ pageParam, signal }) => {
        const routeParams = queryData({ pageParam: pageParam as TPageParameter });
        if (forwardAbortSignal === true) {
          const request = routeParams as ParamsMap<T>[TMethod] & { fetchOptions?: RequestInit };
          return (api[method] as (args: ParamsMap<T>[TMethod]) => Promise<TResponse>)({
            ...request,
            fetchOptions: { ...request.fetchOptions, signal },
          });
        }
        return (api[method] as (args: ParamsMap<T>[TMethod]) => Promise<TResponse>)(routeParams);
      },
      retry: shouldRetry,
      ...(rest as Omit<typeof rest, 'getNextPageParam' | 'getPreviousPageParam'> & {
        getNextPageParam: (lastPage: TResponse, allPages: TResponse[]) => TPageParameter | undefined;
        getPreviousPageParam?: (firstPage: TResponse, allPages: TResponse[]) => TPageParameter | undefined;
      }),
    });
  };

  return { useQuery, useMutation, useInfiniteQuery, getQueryOptions };
}

/**
 * Sets the params for the request, including the account ID and workspace ID
 */
export function contextualParams<T extends Record<string, unknown>>(params: T = {} as T) {
  const accountId = window.__hydra__?.account?.id;
  const workspaceId = getWorkspaceIdFromCurrentUrl();

  return {
    accountId,
    workspaceId,
    ...params,
  };
}

/**
 * gets the base URL for the API based on the current account region
 * and environment.
 */
function getApiUrl() {
  // Default to US for auth and calls made before an account is known
  const region = window.__hydra__?.account?.dc ?? 'us';

  if (__CIO_ENV__ === 'production') {
    return {
      us: 'https://us.fly.customer.io',
      eu: 'https://eu.fly.customer.io',
    }[region];
  } else if (__CIO_ENV__ === 'standalone') {
    return {
      us: 'https://us.fly-standalone.customer.io',
      eu: 'https://us.fly-standalone.customer.io',
    }[region];
  } else {
    return '';
  }
}

type Extract200Response<T> = T extends Promise<infer U>
  ? U extends { status: 200; body: infer B }
    ? Promise<B>
    : never
  : T;

type TransformFunctions<T> = {
  [K in keyof T]: T[K] extends (...args: infer A) => infer R ? (...args: A) => Extract200Response<R> : T[K];
};

/**
 * Modifies the API object to return the 200 response body directly since
 * we throw an error if the response status is not 2xx.
 */
function modifyApiToBeHappyPath<T extends Record<string, unknown>>(object: T): TransformFunctions<T> {
  const result: Record<string, unknown> = {};

  for (const key in object) {
    const value = object[key];
    result[key] =
      typeof value === 'function'
        ? async (...args: unknown[]) => {
            const res = await value(...args);

            return res.body;
          }
        : value;
  }

  return result as TransformFunctions<T>;
}
